import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Button } from '@mui/material';
import { useCategoriesDestroyMutation, useSalonsCategoriesListQuery } from '../../../../services/GLOWClient';
import theme from '../../../../theme';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import DeleteIcon from '@mui/icons-material/Delete';

const DeleteCategory = ({ open, onClose, onSuccess, categoryData }) => {
    const { t } = useTranslation();
    // RTK Query hook for deleting a category
    const [deleteCategory, { isLoading: isDeleting }] = useCategoriesDestroyMutation();
    const [dialogClosed, setDialogClosed] = useState(false);

    const salonId = categoryData?.salon;
    const { data: salonCategoriesData } = useSalonsCategoriesListQuery(
        salonId ? { salonId: Number(salonId) } : undefined,
        { skip: !open || !salonId }
    );

    // Reset dialog state when open/close changes
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
        } else {
            setDialogClosed(false);
        }
    }, [open]);

    const handleDelete = async () => {
        try {
            // Use the correct category ID field - try multiple possible field names
            const directCategoryId = categoryData?.category_id || categoryData?.id || categoryData?.pk;

            const categoryName = (categoryData?.name || '').trim().toLowerCase();
            const resolvedCategoryId = (() => {
                if (directCategoryId) return directCategoryId;
                if (!categoryName) return undefined;

                const list = Array.isArray(salonCategoriesData) ? salonCategoriesData : (salonCategoriesData?.results || []);
                const match = list.find((c) => (c?.name || '').trim().toLowerCase() === categoryName);
                return match?.category_id || match?.id || match?.pk;
            })();

            if (!directCategoryId) {
                console.error('No valid category ID found:', categoryData);
                alert('Erreur: ID de catégorie non trouvé. Détails: ' + JSON.stringify(categoryData));
                return;
            }

            await deleteCategory({ categoryId: directCategoryId }).unwrap();

            // Call onSuccess callback to close the dialog and refresh the list
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Failed to delete category:', error);
        }
    };

    // Content to be displayed in the dialog
    const dialogContent = (
        <div style={{ padding: '16px 0' }}>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                {t('pro_category.delete_category.confirm_message', { name: categoryData?.name })}
            </Typography>
            
            <Typography variant="body2" color="error">
                {t('pro_category.delete_category.irreversible_warning')}
            </Typography>
        </div>
    );
    
    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;
    
    return (
        <MuiDialog
            open={open}
            icon={<DeleteIcon style={{ color: theme.colors.error.main }} />}
            triggerButton={triggerButton}
            title={t('pro_category.delete_category.delete_category_title')}
            subtitle={t('pro_category.delete_category.delete_category_subtitle', { name: categoryData?.name || '' })}
            content={dialogContent}
            existAction={true}
            actionTitle={t('pro_category.delete_category.delete')}
            actionFunction={handleDelete}
            onClose={dialogClosed}
            onExternalClose={onClose}
            isLoading={isDeleting}
            actionType="button"
            maxWidth="sm"
            disableAction={isDeleting}
        />
    );
};

export default DeleteCategory;